import React, { useState } from 'react';
import dayjs from 'dayjs';

import {
  Avatar,
  Button,
  Input,
  List,
  Menu,
  Popover,
  Row,
  Space,
  Typography,
  message,
} from 'antd';
import { MoreOutlined, UserOutlined } from '@ant-design/icons';

import { useClient, useUser } from '../../contexts';

import { ShadowWrapper } from '../index';

import { ResourceType } from './types';
import { HistoryEvent } from '../../types';

import styles from './History.module.css';
import { usePermissions } from 'src/contexts/Permissions/PermissionsCheck';

const { Text, Title } = Typography;
const { Item } = Menu;

export const HistoryView: React.FC<{
  history: Array<HistoryEvent>;
  addNote: (text: string) => void;
  editNote: (id: string, text: string) => void;
  deleteNote: (id: string) => void;
}> = ({ history, addNote, editNote, deleteNote }) => {
  const { hasPermission } = usePermissions();

  const [note, setNote] = useState<string>('');

  const historyArray = (history || []).map((comment: any) => {
    if ((!comment.avatar || comment.name === '') && comment.publisher) {
      comment.avatar = comment.publisher.avatar
        ? comment.publisher.avatar.path
        : '';
    }

    if ((!comment.name || comment.name === '') && comment.publisher) {
      comment.name = comment.publisher.name;
    }

    return comment;
  });

  const submitNote = (note: string) => {
    if (note) {
      addNote(note);
      setNote('');
    }
  };

  return (
    <ShadowWrapper className={styles.root}>
      <Title level={2} className={styles.header}>
        Notes et suivi
      </Title>
      <Space direction="vertical" size="large" className={styles.body}>
        <List
          itemLayout="vertical"
          dataSource={historyArray}
          renderItem={(event: HistoryEvent) => (
            <HistoryItem
              {...event}
              editNote={editNote}
              deleteNote={deleteNote}
            />
          )}
          className={styles.list}
        />
        {hasPermission({ page: 'historyModule', permission: 'create' }) && (
          <>
            <Input.TextArea
              id="comment"
              onChange={({ target: { value } }) => {
                setNote(value);
              }}
              className={styles.input}
              value={note}
              placeholder="Veuillez insérer votre commentaire"
            />
            <Button
              id="note"
              type="primary"
              size="large"
              onClick={() => submitNote(note)}
              className={styles.button}
            >
              Ajouter ma note
            </Button>
          </>
        )}
      </Space>
    </ShadowWrapper>
  );
};

export const HistoryItem: React.FC<
  HistoryEvent & {
    editNote: (id: string, text: string) => void;
    deleteNote: (id: string) => void;
  }
> = ({
  id,
  name,
  avatar,
  text,
  publisher,
  publisher_id,
  created_at,
  editNote,
  deleteNote,
}) => {
  const { user } = useUser();

  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isEditable, setIsEditable] = useState<boolean>(false);

  return (
    <List.Item>
      <Row className={styles.headerNote}>
        <div className={styles.userInfoContainer}>
          <Avatar
            src={avatar}
            icon={<UserOutlined />}
            alt={`Avatar de ${name}`}
            size="large"
          />
          <div className={styles.userInfo}>
            <Text strong>{name}</Text>
            <div style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
              {dayjs(created_at).format('LLL')}
            </div>
          </div>
        </div>
        {(user?.id === publisher?.id || user?.id === publisher_id) && (
          <Popover
            content={
              <Menu style={{ border: 'none' }}>
                <Item>
                  <span
                    onClick={() => {
                      setIsVisible(false);
                      deleteNote(id);
                    }}
                  >
                    Supprimer
                  </span>
                </Item>
                <Item>
                  <span
                    onClick={() => {
                      setIsVisible(false);
                      setIsEditable(true);
                    }}
                  >
                    Editer
                  </span>
                </Item>
              </Menu>
            }
            trigger="click"
            placement="bottom"
            open={isVisible}
          >
            <span
              onClick={() => setIsVisible(!isVisible)}
              style={{ cursor: 'pointer' }}
            >
              <MoreOutlined />
            </span>
          </Popover>
        )}
      </Row>
      <Row>
        {isEditable ? (
          <Input
            defaultValue={text}
            onPressEnter={({ target: { value } }: any) => {
              setIsEditable(false);
              editNote(id, value);
            }}
          />
        ) : (
          <Text strong style={{ color: '#1a95c2' }}>
            {text}
          </Text>
        )}
      </Row>
    </List.Item>
  );
};

export const History: React.FC<{
  resource: ResourceType;
  updateResource: () => void;
}> = ({ resource, updateResource }) => {
  const { user } = useUser();
  const { deletion, patch, post } = useClient();

  if (!resource) {
    return null;
  }

  const addNote = (text: string) => {
    post(
      `/comments`,
      {
        diffusion: {
          id: resource.threadId ? resource.threadId : resource.thread.id,
        },
        publisher: { id: user!.id, model_type: 'user' },
        text,
      },
      () => {
        message.success('La note interne a été créée avec succès');
        updateResource();
      },
      () =>
        message.error(
          `Une erreur est survenue lors de l'ajout de la note interne`
        )
    );
  };

  const editNote = (id: string, text: string) => {
    patch(
      `/comments/${id}`,
      {
        text,
      },
      () => {
        message.success('La note interne a été modifiée avec succès');
        updateResource();
      },
      () =>
        message.error(
          `Une erreur est survenue lors de la modification de la note interne`
        )
    );
  };

  const deleteNote = (id: string) => {
    deletion(
      `/comments/${id}`,
      {},
      () => {
        message.success('La note interne a été supprimée avec succès');
        updateResource();
      },
      () =>
        message.error(
          `Une erreur est survenue lors de la suppression de la note interne`
        )
    );
  };

  return (
    <HistoryView
      history={resource.history ? resource.history : resource?.thread?.comments}
      addNote={addNote}
      editNote={editNote}
      deleteNote={deleteNote}
    />
  );
};
